import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mdiSync, mdiTextBoxOutline, mdiTrashCanOutline, mdiTrayArrowDown } from '@mdi/js';
import Icon from '@mdi/react';
import { Check, CheckCircleOutline, Close, ContentCopyOutlined, DashboardCustomizeOutlined, Edit, ForwardToInboxOutlined, GroupOutlined, ShareOutlined } from '@mui/icons-material';
import clsx from 'clsx';
import moment from 'moment';
import { Prompt, useLocation } from 'react-router-dom';
import _ from 'lodash';
import Constants from 'relevant-shared/reportData/constants';
import { byReportType } from 'relevant-shared/reportData/reportType';
import useReports from '../../hooks/useBreakpoint/useReports';
import { useToast } from '../../hooks/useToast';
import BrowserUtils from '../../lib/browserUtils';
import DateUtils from '../../lib/dateUtils';
import { stores } from '../../stores';
import { useAiStore } from '../../stores/AiStore';
import { useUsers } from '../../stores/userQuery';
import { UserAvatar } from '../Avatar';
import { Badge } from '../Badge';
import { Button, ButtonProps } from '../Button/Button';
import { IconButton } from '../Button/IconButton';
import { MenuItem, MoreMenu } from '../MoreMenu/MoreMenu';
import Report from '../Report';
import { getReportTypeDescription, getReportTypeIcon } from '../ReportsOverview/utils';
import { Tooltip } from '../Tooltip';
import ReportSharing from '../TopBar/ReportSharingMenuButton/ReportSharing';
import { SubscriptionsEditor } from './Subscriptions/SubscriptionsEditor';
import { TopBar } from '../TopBar/TopBar';
import { Cooltip } from './Cooltip';
import { usePinReport } from './hooks/usePinReport';
import useReportActions from './hooks/useReportActions';
import { PinButton } from './PinButton';
import { Reports } from '../../api/relevant';
import { LoadingIndicator } from '../LoadingIndicator';
import { base64Utf8Encode } from 'relevant-shared/misc/misc';
import { Subscription } from './Subscriptions/types';
import { useFloating, useInteractions, useDismiss, useRole, offset, shift, autoUpdate, FloatingPortal, useTransitionStyles } from '@floating-ui/react';
import { TabView } from '../Tabs/TabView';

export function ViewEditReport({
	report,
	defaultSettings,
	type,
	noSave,
	isNew,
	warnOldData,
}: {
	report: Reports;
	defaultSettings: object;
	type: string;
	noSave: boolean;
	isNew: boolean;
	warnOldData: boolean;
}) {
	const [isPublic, setIsPublic] = useState(report?.isPublic);
	const [isDirty, setIsDirty] = useState(isNew);

	// HACK: For now, subscribe to AiStore pinned width state to make graphs update when pinned width changes
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { pinnedWidth } = useAiStore();

	const fallback = useMemo(() => defaultSettings || ({
		showTable: true,
		start: DateUtils.fullDay(new Date(), -31),
		end: DateUtils.fullDay(new Date(), -1),
		type,
		...Constants[type]?.getDefaultProperties?.(true),
	}), [defaultSettings, type]);

	const [status, setStatus] = useState();
	const [settingsOpen, setSettingsOpen] = useState(report?.settings?.settingsOpen ?? fallback.settingsOpen ?? isNew);
	// TODO: This solution of having the report component as a ref
	// is weird and should be removed at some point.
	const reportComponentRef = useRef<Report>();
	const popoverAnchor = useRef(null);
	const [sharingOpen, setSharingOpen] = useState(false);
	const { data: users } = useUsers();
	useReports();

	const [title, setTitle] = useState(report?.settings?.title ?? 'My report');
	const handleSetTitle = useCallback((value: string) => {
		setTitle(value);
		reportComponentRef.current?.setState({
			title: value,
		});
		setIsDirty(true);
	}, [setTitle, reportComponentRef, setIsDirty]);

	const { pinned, setPinned } = usePinReport({ report });

	const {
		onSave,
		onDelete,
		onExport,
		onSaveTemplate,
		onCopyToDashboard,
		onCopy,
		subscriptions,
		setSubscriptions,
	} = useReportActions({
		isPrivate: !isPublic,
		isDirty: isDirty && !isNew,
		report,
		type,
		pinnedByUsers: report?.pinnedByUsers || [],
		reportComponentRef,
		setStatus,
	});
	const [, showToast] = useToast();

	useEffect(() => {
		if (status === 'saved') {
			// showToast({ message: 'Report saved', status: 'success', timeout: 3 });
		} else if (status === 'saveError') {
			showToast({ message: 'Failed to save report. Please try again.', status: 'error' });
		}
	}, [status, showToast]);

	const reportSettings = report?.settings || fallback;

	const getShareUrl = useCallback(async () => {
		if (!reportComponentRef.current) {
			return null;
		}
		const settings = reportComponentRef.current.getCurrentProperties(true);
		return await Reports.call('getShareUrl', { settings });
	}, [reportComponentRef]);

	useEffect(() => {
		if (status === 'saved') {
			// Reset dirty state after saving
			setIsDirty(false);
		}
	}, [status]);

	const author = users?.find((user) => user.id === report?.userId);

	return (
		<div className="w-full flex justify-between flex-col gap-4">
			<TopBar
				title={title}
				smartHelp={{
					query: `${Constants[type]?.DESCRIPTION} report`,
					question: `How do ${Constants[type]?.DESCRIPTION} reports work? Mention dimensions, metrics, filters, etc and how they differ from other report types. Title: ${Constants[type]?.DESCRIPTION}`,
				}}
			/>

			<Prompt
				when={isDirty && !isNew}
				message="You have unsaved changes. Are you sure you want to leave?"
			/>

			<div className="flex flex-col w-full gap-2">
				<div className="flex justify-between gap-2 items-center">
					<div className="flex items-center z-0 shrink min-w-0">
						{!Constants[type]?.noSaveType && (
							<Cooltip
								content={(
									<div className="flex flex-col gap-4">
										<div className="flex items-center gap-2">
											<img src={getReportTypeIcon(type)} className="size-6" />
											<span className="text-sm font-semibold">{Constants[type]?.DESCRIPTION}</span>
										</div>
										{getReportTypeDescription(type) && (
											<span className="text-sm text-text-secondary">{getReportTypeDescription(type)}</span>
										)}
										<Button variant="ghost" size="xs" href={`/reports?type=${type}`}>View all {Constants[type]?.DESCRIPTION} reports</Button>
									</div>
								)}
							>
								<img src={getReportTypeIcon(type)} className="size-6" />
							</Cooltip>
						)}
						<ReportTitleInput
							title={title}
							setTitle={handleSetTitle}
							className="ml-2"
						/>
						{!isNew && (
							<PinButton
								variant="favourite"
								className="z-[-1] [&:not([data-active]):not(:hover)]:text-grey-400 hover:bg-transparent! active:bg-transparent!"
								active={pinned}
								onToggle={setPinned}
							/>
						)}
					</div>
					<div className="flex items-center gap-4 pr-4">
						{/* TODO: Update <Badge> component once design system is updated. Unique for now. */}
						{isDirty && (
							<div
								className={clsx(
									'inline-flex items-center gap-1 w-fit rounded-md pl-1 pr-2 py-1 text-xs h-[24px]',
									'bg-component-badge-bg-yellow text-component-badge-fg-yellow border border-component-badge-borders-yellow',
								)}
							>
								<Icon path={mdiSync} className="size-4 inline" /> Unsaved changes
							</div>
						)}
						<Cooltip
							content={(
								<div className="flex flex-col gap-4">
									{isPublic ? (
										<>
											<Badge color="yellow">Public report</Badge>
											<span className="text-sm text-text-secondary">This report is shared with the entire organisation. Everyone is able to view and edit it.</span>
											<Button variant="ghost" size="xs" href="/reports?tab=shared">View shared reports</Button>
										</>
									) : (
										<>
											<Badge color="yellow">Private report</Badge>
											<span className="text-sm text-text-secondary">This report is only available to you.</span>
											<Button variant="ghost" size="xs" href="/reports?tab=my">View your reports</Button>
										</>
									)}
								</div>
							)}
						>
							<GroupOutlined
								className={
									clsx(
										' size-[20px]!',
										isPublic ? 'text-fg-primary' : 'text-fg-primary_disabled',
									)
								}
							/>
						</Cooltip>
						<Cooltip
							content={(
								<div className="flex flex-col gap-4">
									<Badge color="yellow">Subscriptions</Badge>
									<span className="text-sm text-text-secondary"><span className="font-semibold">{subscriptions.length} {subscriptions.length === 1 ? 'person is' : 'people are'}</span> registered to receive this report on a regular basis.</span>
									<Button variant="ghost" size="xs" onClick={() => { setSharingOpen(true); }}>Manage subscriptions</Button>
								</div>
							)}
						>
							<div className="relative size-[20px] leading-none">
								{subscriptions.length > 0 ? (
									<>
										<ForwardToInboxOutlined className="text-fg-primary size-[20px]!" />
										<div className="absolute -top-[1px] -right-[1px] bg-cherry-500 size-2 rounded-full" />
									</>
								) : (
									<ForwardToInboxOutlined className="text-fg-primary_disabled size-[20px]!" />
								)}
							</div>
						</Cooltip>
					</div>
				</div>
				<div className="flex flex-wrap justify-between gap-x-2 items-center *:min-h-10">
					<div className="flex items-center gap-2 shrink-0">
						{report?.userId && (
							<Cooltip
								placement="bottom"
								content={(
									<div className="flex flex-col gap-2">
										<span className="text-sm text-text-secondary">
											Report created by <span className="font-semibold">{author?.fullname}</span> on {moment(report?.createdDate).format('LLL')}
										</span>
										{report.userId === stores.identity.userId() ? (
											<Button variant="ghost" size="xs" href="/reports?tab=my">View all your reports</Button>
										) : (
											<Button variant="ghost" size="xs" href={`/reports?f=${author?.fullname}`}>View all reports by {author?.fullname}</Button>
										)}
									</div>
								)}
							>
								<div className="flex items-center gap-4">
									<div className="flex items-center gap-2">
										<UserAvatar userId={report?.userId} className="min-w-0! min-h-0! size-5!" />
										<span className="text-sm text-text-secondary">{author?.fullname}</span>
									</div>
									<span className="text-xs text-text-tertiary">Created: {moment(report?.createdDate).format('LL')}</span>
								</div>
							</Cooltip>
						)}
					</div>
					<div id="report-tools" className="flex gap-4 justify-end grow">
						<div className="flex gap-2 items-center">
							<Tooltip title="Download">
								<IconButton icon={<Icon path={mdiTrayArrowDown} />} onClick={onExport} />
							</Tooltip>
							<Tooltip title="Copy to Dashboard">
								<IconButton icon={<DashboardCustomizeOutlined />} onClick={onCopyToDashboard} />
							</Tooltip>
							<Tooltip title="Sharing">
								<IconButton
									ref={popoverAnchor}
									icon={<ShareOutlined />}
									onClick={() => { setSharingOpen(!sharingOpen); }}
									active={sharingOpen}
								/>
							</Tooltip>
							<ReportSharingAndSubscriptions
								open={sharingOpen}
								onClose={() => setSharingOpen(false)}
								anchor={popoverAnchor}
								getShareUrl={getShareUrl}
								isPublic={isPublic}
								onIsPublicChange={() => { setIsPublic(!isPublic); setIsDirty(true); }}
								subscriptions={subscriptions}
								setSubscriptions={setSubscriptions}
								reportComponentRef={reportComponentRef}
								setIsDirty={setIsDirty}
							/>
							<MoreMenu>
								{stores.identity.isAdministrator() && (
									<MenuItem icon={<Icon path={mdiTextBoxOutline} />} onClick={onSaveTemplate}>Save as Template</MenuItem>
								)}
								<MenuItem icon={<ContentCopyOutlined />} onClick={onCopy} disabled={isNew}>
									Make a copy
								</MenuItem>
								<MenuItem icon={<Icon path={mdiTrashCanOutline} />} onClick={onDelete} className="hover:text-fg-highlight" disabled={isNew}>Delete</MenuItem>
							</MoreMenu>
						</div>
						<div className="flex shrink-0 gap-4">
							<Button
								variant="outlined"
								icon={<Edit />}
								onClick={() => setSettingsOpen(true)}
							>
								Edit
							</Button>
							<ReportSaveButton
								onSave={onSave}
								disabled={noSave}
							/>
						</div>
					</div>
				</div>
			</div>

			<Report
				ref={reportComponentRef}
				onSettingsOpenToggle={(isOpen) => setSettingsOpen(isOpen)}
				onTitleChange={handleSetTitle}
				isActualReport
				{...reportSettings}
				settingsOpen={settingsOpen}
				warnOldData={warnOldData}
				onSettingsChanged={() => setIsDirty(true)}
			/>
		</div>
	);
}

export function ReportSaveButton({
	label = 'Save changes',
	minDuration = 1,
	successLabel = 'Saved',
	successDuration = 2,
	onSave,
	...props
}: {
	label?: string;
	// Minimum duration of the loading animation
	minDuration?: number; // seconds
	successLabel?: string;
	successDuration?: number;
	onSave: () => Promise<boolean>;
} & ButtonProps) {
	const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');

	const handleClick = useCallback(async () => {
		if (state !== 'idle') {
			return;
		}
		setState('saving');
		try {
			const [saved] = await Promise.all([
				onSave(),
				new Promise((resolve) => { setTimeout(resolve, minDuration * 1000); }),
			]);
			if (saved && successLabel) {
				setState('saved');
				setTimeout(() => {
					setState('idle');
				}, successDuration * 1000);
			} else {
				setState('idle');
			}
		} catch (error) {
			console.error(error);
			setState('idle');
		}
	}, [onSave, state, minDuration, successLabel, successDuration]);

	return (
		<Button
			onClick={handleClick}
			className="relative"
			{...props}
		>
			{state === 'saving' && (
				<div className="absolute inset-0 flex items-center justify-center animate-fade-in">
					<LoadingIndicator />
				</div>
			)}
			<div className={clsx(
				'absolute inset-0 flex gap-2 items-center justify-center transition-all duration-400',
				state === 'saved' ? 'opacity-100' : 'opacity-0',
			)}>
				<Check className="size-4" />
				<span>{successLabel}</span>
			</div>
			<span
				className={
					clsx(
						'transition-opacity duration-400',
						state !== 'idle' && 'opacity-0',
					)
				}
			>
				{label}
			</span>
		</Button>
	);
}

export function ReportTitleInput({
	title,
	setTitle,
	className,
}: {
	title: string;
	setTitle: (value: string) => void;
	className?: string;
}) {
	const [value, setValue] = useState(title);

	useEffect(() => {
		if (value !== title) {
			setValue(title);
		}
	}, [title]);

	return (
		<div className={clsx('relative min-w-0 max-w-full group shrink', className)}>
			<div className="visible px-1 py-1 text-xl font-medium text-text-primary border border-transparent truncate whitespace-pre min-h-[38px] min-w-4">{value}</div>
			<input
				type="text"
				className="peer absolute inset-0 px-1 py-1 text-xl font-medium text-text-primary bg-white invisible group-hover:visible focus:visible active:visible rounded-sm border border-transparent hover:border-borders-primary focus:border-borders-primary min-w-0"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				onBlur={() => {
					if (value === '') {
						setValue(title);
					} else {
						setTitle(value);
					}
				}}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						e.preventDefault();
						setTitle(value);
						e.target.blur();
					} else if (e.key === 'Escape') {
						e.preventDefault();
						setValue(title);
						e.target.blur();
					}
				}}
			/>
		</div>
	);
}

export function ReportSharingAndSubscriptions({ open, onClose, anchor, getShareUrl, isPublic, onIsPublicChange, subscriptions, setSubscriptions, reportComponentRef, setIsDirty }: {
	open: boolean;
	onClose: () => void;
	anchor: React.RefObject<HTMLDivElement>;
	getShareUrl: () => Promise<string>;
	isPublic: boolean;
	onIsPublicChange: () => void;
	subscriptions: Subscription[];
	setSubscriptions: (value: Subscription[]) => void;
	reportComponentRef: React.RefObject<Report>;
	setIsDirty: (isDirty: boolean) => void;
}) {
	const [activeTab, setActiveTab] = useState(0);
	const [isDirtyInternal, setIsDirtyInternal] = useState(false);
	const [shareUrl, setShareUrl] = useState(null);
	const onTabChange = useCallback((index: number) => {
		setActiveTab(index);
	}, []);

	const handleClose = () => {
		setIsDirtyInternal(false);
		onClose();
	};

	const { refs, floatingStyles, context } = useFloating({
		open,
		onOpenChange: (isOpen) => {
			if (!isOpen) {
				handleClose();
			}
		},
		placement: 'bottom',
		middleware: [
			offset({ mainAxis: 8 }),
			shift({ padding: 5 }),
		],
		whileElementsMounted: autoUpdate,
	});

	const dismiss = useDismiss(context, { outsidePress: !isDirtyInternal });
	const role = useRole(context);

	const { getFloatingProps } = useInteractions([
		dismiss,
		role,
	]);

	const { isMounted, styles: transitionStyles } = useTransitionStyles(context, { duration: 100 });

	// Set the reference element to the anchor
	React.useEffect(() => {
		if (anchor.current) {
			refs.setReference(anchor.current);
		}
	}, [anchor, refs]);

	useEffect(() => {
		if (open) {
			setShareUrl(null);
			getShareUrl().then(setShareUrl);
		}
	}, [open, getShareUrl]);

	return (
		<FloatingPortal>
			{isMounted && (
				<div
					ref={refs.setFloating}
					style={{
						...floatingStyles,
						...transitionStyles,
					}}
					{...getFloatingProps()}
					className="box-border w-[500px] bg-white rounded-lg shadow-lg border border-borders-primary z-[var(--z-tooltip)] outline-none mt-1 overflow-x-hidden transition-opacity"
				>
					<div className="flex justify-between border-b border-borders-primary px-6 py-4">
						<div className="flex gap-4 items-center">
							<button
								type="button"
								className={clsx(
									'px-2 text-base transition-colors hover:text-text-primary',
									activeTab === 0 ? 'text-text-primary font-medium' : 'text-text-secondary',
								)}
								onClick={() => onTabChange(0)}
							>
								Sharing
							</button>
							<button
								type="button"
								className={clsx(
									'px-2 text-base transition-colors hover:text-text-primary',
									activeTab === 1 ? 'text-text-primary font-medium' : 'text-text-secondary',
								)}
								onClick={() => onTabChange(1)}
							>
								Subscriptions{subscriptions.length > 0 && ` (${subscriptions.length})`}
							</button>
						</div>
						<IconButton size="m" icon={<Close />} onClick={handleClose} />
					</div>

					<TabView index={activeTab}>
						<ReportSharing
							shareUrl={shareUrl}
							isPublic={isPublic}
							onIsPublicChange={onIsPublicChange}
						/>
						<SubscriptionsEditor
							subscriptions={subscriptions}
							hasDynamicDateRange={(() => {
								const settings = reportComponentRef.current.getCurrentProperties();
								return Boolean(settings.dynamicDateRange)
									|| (!Number.isNaN(settings.start) && settings.start < 0)
									|| (!Number.isNaN(settings.end) && settings.end < 0);
							})()}
							onSubscriptionsChange={(subs) => { setSubscriptions(subs); setIsDirty(true); }}
							isDirty={isDirtyInternal}
							setIsDirty={setIsDirtyInternal}
						/>
					</TabView>
				</div>
			)}
		</FloatingPortal>
	);
}
